/**
 * OfflineGuideDialog
 * Explains how offline verification works in 3 simple steps.
 * Shown from the Getting Started checklist, the Start Verification dialog,
 * and the Offline Sync page.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Alert
} from '@mui/material';
import {
  SignalWifiOff,
  CloudDownload,
  CameraAlt,
  CloudUpload,
  CheckCircle,
  GpsFixed,
  Mic,
  Description,
  Draw
} from '@mui/icons-material';
import { brand } from '../../theme/colors';

const GUIDE_STEPS = [
  {
    label: 'Prepare Before You Go',
    icon: <CloudDownload />,
    description: 'While you still have internet, open the verification and tap "Prepare for Offline". This downloads everything you need — site coordinates, checklist, project details — to your phone.',
    tips: ['Do this at home or office before heading to the site', 'Takes about 10 seconds with a good connection', 'You\'ll see a green "Ready for Offline" badge when done']
  },
  {
    label: 'Complete On-Site (No Internet Needed)',
    icon: <SignalWifiOff />,
    description: 'At the construction site, open the verification and complete it entirely offline. GPS, photos, voice notes, checklist, report, and your digital signature — all saved locally on your device.',
    features: [
      { icon: <GpsFixed />, label: 'GPS proof auto-captured' },
      { icon: <CameraAlt />, label: 'Photos with location' },
      { icon: <Mic />, label: 'Voice notes' },
      { icon: <Description />, label: 'Draft report' },
      { icon: <Draw />, label: 'Digital signature' }
    ]
  },
  {
    label: 'Sync When You\'re Back Online',
    icon: <CloudUpload />,
    description: 'When you have internet again, the app automatically uploads your photos, voice notes, and verification bundle. The server validates everything and generates the final AI-enhanced report.',
    tips: ['Sync happens automatically — just connect to WiFi or data', 'You can also tap "Sync Now" from the status bar', 'Check progress at Offline Sync in your menu']
  }
];

const OfflineGuideDialog = ({ open, onClose, onPrepareOffline }) => {
  const [activeStep, setActiveStep] = useState(0);

  const handleClose = () => {
    setActiveStep(0);
    // Mark as seen in localStorage
    localStorage.setItem('eqaya_offline_guide_seen', 'true');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <SignalWifiOff sx={{ color: brand.accent || '#43B581' }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Offline Verification
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Complete verifications even without internet
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          Zimbabwe's internet can be unreliable, especially at construction sites.
          This feature lets you do everything on-site without any connection.
        </Alert>

        <Stepper activeStep={activeStep} orientation="vertical">
          {GUIDE_STEPS.map((step, index) => (
            <Step key={index} expanded>
              <StepLabel
                StepIconProps={{
                  sx: {
                    '&.Mui-completed': { color: brand.accent || '#43B581' },
                    '&.Mui-active': { color: brand.accent || '#43B581' }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {step.icon}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                </Box>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {step.description}
                </Typography>

                {/* Feature chips for step 2 */}
                {step.features && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                    {step.features.map((feat, fi) => (
                      <Chip
                        key={fi}
                        icon={feat.icon}
                        label={feat.label}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: brand.accent || '#43B581' }}
                      />
                    ))}
                  </Box>
                )}

                {/* Tips */}
                {step.tips && (
                  <Box sx={{ pl: 1 }}>
                    {step.tips.map((tip, ti) => (
                      <Box key={ti} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                        <CheckCircle sx={{ fontSize: 14, mt: 0.3, color: brand.accent || '#43B581' }} />
                        <Typography variant="caption" color="text.secondary">{tip}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {/* Integrity callout */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            How is integrity maintained?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Every photo, GPS reading, and action is cryptographically hashed and chained together.
            When you sync, the server re-validates everything — GPS distance from the site, timestamps,
            and tamper detection. This ensures the verification is trustworthy even though it happened offline.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>
          Got It
        </Button>
        {onPrepareOffline && (
          <Button
            variant="contained"
            startIcon={<CloudDownload />}
            onClick={() => {
              handleClose();
              onPrepareOffline();
            }}
            sx={{ bgcolor: brand.accent || '#43B581', '&:hover': { bgcolor: '#3aa575' } }}
          >
            Prepare for Offline Now
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OfflineGuideDialog;
